import { getSpell } from '@character-creator/data';

export interface AttackProfile {
  martialAttacks: number;
  spellAttacks: number;
  cantripsCount: number;
  hasSpellSlots: boolean;
  isCaster: boolean;
}

export type CombatLayoutType = 'combat-priority' | 'tab-within-tab';

/**
 * Count martial attacks from attack list
 * (Simpler than parsing equipment since attacks are already calculated)
 */
export function countMartialAttacks(attackCount: number): number {
  return attackCount;
}

/**
 * Check if a spell is a combat spell (requires attack roll or saving throw)
 */
function isCombatSpell(spellId: string): boolean {
  const spell = getSpell(spellId);
  if (!spell) return false;

  // Utility spells that shouldn't count as combat spells
  const utilitySpells = [
    'light',
    'mage-hand',
    'prestidigitation',
    'minor-illusion',
    'mending',
    'message',
    'dancing-lights',
    'friends',
    'thaumaturgy',
    'mage-armor',
    'shield',
    'detect-magic',
    'identify',
    'comprehend-languages',
    'feather-fall',
    'unseen-servant',
  ];

  if (utilitySpells.includes(spell.id)) {
    return false;
  }

  // Combat spells typically have damage keywords or force effects
  const description = spell.description.toLowerCase();
  const hasCombatKeywords =
    description.includes('damage') ||
    description.includes('attack') ||
    description.includes('save') ||
    description.includes('hit points') ||
    description.includes('creature') && (description.includes('make') || description.includes('must'));

  // Schools that are typically offensive
  const offensiveSchools = ['evocation', 'necromancy', 'conjuration'];
  const isOffensiveSchool = offensiveSchools.includes(spell.school);

  return hasCombatKeywords || (isOffensiveSchool && spell.level > 0);
}

/**
 * Count combat-relevant spells from spell list
 */
export function countSpellAttacks(spellIds: string[]): number {
  return spellIds.filter((spellId) => isCombatSpell(spellId)).length;
}

/**
 * Count cantrips from spell list
 */
export function countCantrips(spellIds: string[]): number {
  return spellIds.filter((spellId) => {
    const spell = getSpell(spellId);
    return spell && spell.level === 0;
  }).length;
}

/**
 * Check if character has any spell slots available
 */
export function hasAnySpellSlots(spellSlots: Record<string, { max: number; used: number }> | undefined): boolean {
  if (!spellSlots) return false;
  return Object.values(spellSlots).some((slot) => slot.max > 0);
}

/**
 * Build attack profile from component data
 */
export function buildAttackProfile(
  attackCount: number,
  spellIds: string[],
  spellSlots: Record<string, { max: number; used: number }> | undefined
): AttackProfile {
  const martialAttacks = countMartialAttacks(attackCount);
  const spellAttacks = countSpellAttacks(spellIds);
  const cantripsCount = countCantrips(spellIds);
  const hasSpellSlots = hasAnySpellSlots(spellSlots);

  const isCaster = determineCasterStatus(
    martialAttacks,
    spellAttacks,
    cantripsCount,
    hasSpellSlots
  );

  return {
    martialAttacks,
    spellAttacks,
    cantripsCount,
    hasSpellSlots,
    isCaster,
  };
}

/**
 * Determine if character is classified as a caster
 */
function determineCasterStatus(
  martialAttacks: number,
  spellAttacks: number,
  cantripsCount: number,
  hasSpellSlots: boolean
): boolean {
  // Rule 1: More spell attacks than martial attacks
  if (spellAttacks > martialAttacks) {
    return true;
  }

  // Rule 2: Has 3+ cantrips (indicates primary caster)
  if (cantripsCount >= 3) {
    return true;
  }

  // Rule 3: Has spell slots but no martial attacks
  if (hasSpellSlots && martialAttacks === 0) {
    return true;
  }

  return false;
}

/**
 * Detect the optimal combat layout based on attack and spell data
 */
export function detectCombatLayout(
  attackCount: number,
  spellIds: string[],
  spellSlots: Record<string, { max: number; used: number }> | undefined
): CombatLayoutType {
  const profile = buildAttackProfile(attackCount, spellIds, spellSlots);
  return profile.isCaster ? 'tab-within-tab' : 'combat-priority';
}
